import { useRouteError, useNavigate } from "react-router-dom";
import { Home as HomeIcon, ArrowBack as ArrowBackIcon } from "@mui/icons-material";

export default function ErrorPage() {
    const error = useRouteError();
    const navigate = useNavigate();
    console.error(error);

    const handleGoHome = () => {
        navigate("/signIn");
    };

    const handleGoBack = () => {
        navigate(-1);
    };

    return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4 md:p-8">
            <div className="w-full max-w-5xl bg-white rounded-2xl md:rounded-3xl shadow-2xl overflow-hidden">
                <div className="flex flex-col lg:flex-row">
                    <div className="w-full lg:w-1/2 flex items-center justify-center p-8 md:p-12 lg:p-16">
                        <div className="w-full max-w-md">
                            <div className="mb-6 flex justify-center lg:justify-start">
                                <div className="relative w-48 h-48">
                                    <svg viewBox="0 0 200 200" className="w-full h-full">
                                        <circle cx="100" cy="100" r="90" fill="#EBF5FF" />
                                        
                                        <text x="100" y="105" textAnchor="middle" fill="#135e96" fontSize="52" fontWeight="bold" fontFamily="Arial, sans-serif">
                                            404
                                        </text>
                                        {/* Warning Icon - Triangle */}
                                        <path d="M 40 65 L 50 45 L 60 65 Z" fill="none" stroke="#135e96" strokeWidth="3" strokeLinejoin="round"/>
                                        <line x1="50" y1="50" x2="50" y2="57" stroke="#135e96" strokeWidth="2" strokeLinecap="round"/>
                                        <circle cx="50" cy="61" r="1.5" fill="#135e96"/>
                                        
                                        {/* Decorative Dots */}
                                        <circle cx="30" cy="130" r="3" fill="#135e96" opacity="0.3"/>
                                        <circle cx="170" cy="130" r="3" fill="#135e96" opacity="0.3"/>
                                        <circle cx="100" cy="160" r="2" fill="#F59E0B" opacity="0.4"/>
                                    </svg>
                                </div>
                            </div>

                            {/* Error Title */}
                            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 text-center lg:text-left">
                                Page Not Found
                            </h1>

                            {/* Error Message */}
                            <p className="text-base md:text-lg text-gray-600 mb-6 leading-relaxed text-center lg:text-left">
                                Sorry, the page you're looking for doesn't exist or has been moved.
                            </p>

                            {/* Technical Error Details */}
                            {error && (error.statusText || error.message) && (
                                <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-r-lg">
                                    <p className="text-sm text-red-700 font-medium">
                                        Error: {error.statusText || error.message}
                                    </p>
                                </div>
                            )}

                            {/* Action Button */}
                            <div className="mb-6">
                                <button
                                    onClick={handleGoHome}
                                    className="flex items-center justify-center gap-2 px-8 py-3 brand-btn-primary rounded-xl transition-all duration-200 transform hover:scale-105 text-base font-semibold shadow-lg hover:shadow-xl w-full sm:w-auto"
                                >
                                    <ArrowBackIcon className="h-5 w-5" />
                                    Go Back to Sign In
                                </button>
                            </div>

                            {/* Help Text */}
                            <div className="text-center lg:text-left">
                                <p className="text-sm text-gray-500 mb-2">
                                    <span className="font-semibold">Need help?</span> Contact your system administrator
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Blue Design Section - Right - Hidden on Mobile */}
                    <div className="hidden lg:flex w-1/2 relative overflow-hidden brand-sidebar items-center justify-center p-12">
                        {/* Content */}
                        <div className="relative z-10 text-white text-center max-w-sm">
                            {/* Icon */}
                            <div className="mb-8 flex justify-center">
                                <div className="w-24 h-24 bg-white bg-opacity-20 rounded-full flex items-center justify-center backdrop-blur-sm">
                                    <svg
                                        className="w-12 h-12 text-white"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth="2"
                                            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                                        />
                                    </svg>
                                </div>
                            </div>

                            {/* Title */}
                            <h2 className="text-3xl md:text-4xl font-bold mb-4">
                                Lost in Space?
                            </h2>

                            {/* Description */}
                            <p className="text-lg leading-relaxed opacity-90 mb-6">
                                The page you're trying to reach is currently unavailable.
                            </p>

                            {/* Features List */}
                            <div className="space-y-3 text-left">
                                <div className="flex items-center gap-3">
                                    <div className="w-2 h-2 bg-white rounded-full"></div>
                                    <span className="text-base">Check the URL for typos</span>
                                </div>
                                <div className="flex items-center gap-3">
                                    <div className="w-2 h-2 bg-white rounded-full"></div>
                                    <span className="text-base">Use the navigation menu</span>
                                </div>
                                <div className="flex items-center gap-3">
                                    <div className="w-2 h-2 bg-white rounded-full"></div>
                                    <span className="text-base">Return to sign in page</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}